import type { Job } from '../../lib/api'

/** Main board, EXP-002. The server assigns the day's reading. */
export const DAILY_READING_RULE = 'The first reading of each day is the one your baseline learns from. Later readings are compared, not added.'

/** EXP-007: fixed interface copy from the approved ChooseActivity board. */
export const EXPERIMENT_WORDS = {
  eyebrow: 'Experiments · New',
  title: 'Try something, then see what changed',
  lead: 'A reading before, the activity, a short settle, a reading after. You say how you feel before you see the result. Repeat it on other days, and do a quiet-rest day, before anything is called a pattern.',
  oneActivity: 'One activity',
  protocol: 'How it will run',
  before: 'Before',
  activity: 'Activity',
  settle: 'Settle',
  after: 'After',
  report: 'How you feel · before the result',
  result: 'Result · closed',
  reading: 'reading',
  start: 'Start · before reading',
  customHelp: 'A custom activity gets a name and a settle time; it is compared the same way.',
  manualCompletion: 'Finish when you are done',
  exploratory: 'Limits shown with the result',
  loading: 'Loading the activity library…',
  libraryError: 'The activity library could not be read.',
  libraryEmpty: 'There are no activities to choose from yet.',
  countsLoading: 'Loading previous experiments…',
  countsError: 'Previous experiments could not be counted.',
  startError: 'The experiment could not be started.',
  customName: 'Activity name',
  customSettle: 'Settle time (seconds)',
  runsSoFar: 'runs so far',
  controlDays: 'days',
  stop: 'Stop the experiment',
} as const

/** Activity descriptions are presentation copy; names, durations and limits are payload fields. */
export const EXPERIMENT_ACTIVITY_COPY = {
  short_walk: 'A good first experiment: easy to repeat, little sweat, settles fast.',
  quiet_rest: 'The control. Sit without a task. Run it on other days, so the walk has something to be compared with.',
  slow_breathing: 'Paced breathing, eyes open.',
} as const

export const EXPERIMENT_NOTES = [
  {
    title: 'Same conditions',
    body: 'Same headband, same chair, eyes closed both times. Keep the headband on through the walk if you can; if you take it off, the result notes the refit.',
  },
  {
    title: "Your day's reading is untouched",
    body: 'Neither reading enters your baseline. Experiments are kept on their own, so trying things never moves "usual".',
  },
  {
    title: 'The timing is fixed',
    body: "The settle time and the reading length are part of the protocol, so runs on different days can be compared. The app counts them down; you don't have to.",
  },
] as const

export const EXPERIMENT_RUN_WORDS = {
  loading: 'Loading the experiment…',
  activity: 'Do the activity',
  settle: 'Time to settle',
  readyBefore: 'Before reading',
  readyAfter: 'After reading',
  journal: 'How are you right now?',
  eyesClosed: 'Eyes closed. Stay still. A tone will tell you when the reading ends.',
  activityDone: 'I have finished the activity',
  settleDone: 'Continue to the after reading',
  start: 'Start the reading',
  retry: 'Try again',
  lost: 'This reading was lost',
  lostBody: 'The attempt is kept as lost. It is never stitched to another reading.',
  unusable: 'This reading could not be used',
  unusableBody: 'The attempt is kept. A new attempt starts a new reading; nothing is stitched together.',
  windowClosed: 'A full reading cannot start in the remaining window. What was captured is kept.',
  incomplete: 'Experiment incomplete',
  cancelled: 'Experiment stopped',
  terminalBody: 'What was captured is kept. No comparison is shown here.',
  stop: 'Stop the experiment',
  report: 'report sheet — EXP-008',
  sending: 'Checking and sending the reading',
  reconnect: 'Reconnect and check the fit before starting this reading.',
  sync: 'Check the experiment',
  deviation: 'Did it go as planned?',
  shorter: 'Shorter',
  longer: 'Longer',
  other: 'Something else',
  noClock: 'The experiment timing could not be confirmed. Check it again before continuing.',
  protocolUnavailable: 'This run uses an activity protocol version that is unavailable. The run is kept; check again before continuing.',
  savingStop: 'Saving the reading before stopping…',
} as const

/**
 * The phase, in words, without inventing one.
 *
 * `pending` is genuinely "not started", and the backend's own phases are
 * `loading`, `cleaning`, `cleaned`, `scoring`, `persisting`. An unrecognised
 * phase renders as itself rather than as a guess — a new phase means the backend
 * gained a step, and inventing a name for it would be the client narrating work
 * it does not know about.
 */
export const PHASE_WORDS: Readonly<Record<string, string>> = {
  loading: 'Reading the archive',
  cleaning: 'Cleaning the signal',
  cleaned: 'Signal cleaned',
  scoring: 'Scoring against your baseline',
  persisting: 'Saving the result',
  done: 'Finishing up',
}

export function phaseLabel(job: Job): string {
  if (job.status === 'pending') return 'Waiting for a processor'
  if (job.phase == null) return 'Processing'
  return PHASE_WORDS[job.phase] ?? job.phase
}
